import { tokens } from '@env0/ui-theme';
import { primaryFontFamily, type Env0Theme } from 'constants/themes.constants';

/**
 * ThoughtSpot renders in a cross-origin iframe and its `--ts-var-*` bridge takes resolved values, not
 * `var(--…)`, so the embed palette reads tokens here rather than through custom properties. 106
 * variables and 13 `rules_UNSTABLE` selectors are driven by these 26 slots.
 *
 * The switch-off half returns the exact `Env0Theme` keys the mapping read before APO-481, which is what
 * keeps the embeds identical with the design switch off (D7.7). APO-484 deletes that branch.
 *
 * Two legacy keys are split across two slots each — `lightGray` into a surface and a border,
 * `primaryBlue` into text and a control fill — so the new design can use the right kind of token for
 * each job. Parity is unaffected: both halves return the same legacy value.
 */
export type ThoughtspotPalette = {
  rootText: string;
  bodyText: string;
  headingText: string;
  mutedText: string;
  pageSurface: string;
  cardSurface: string;
  elementSurface: string;
  hoverSurface: string;
  activeSurface: string;
  controlBorder: string;
  chipHoverSurface: string;
  separator: string;
  selectionText: string;
  controlAccent: string;
  selectionSurface: string;
  errorSurface: string;
  errorText: string;
  errorBorder: string;
  disabledSurface: string;
  mutedIcon: string;
  onAction: string;
  action: string;
  actionHover: string;
  actionActive: string;
  radius: string;
  shadow: string;
  fontFamily: string;
};

export const thoughtspotPalette = (theme: Env0Theme): ThoughtspotPalette => {
  if (!theme.isNewDesign) {
    return {
      rootText: theme.textColor,
      bodyText: theme.textGray,
      headingText: theme.textBaseGray,
      mutedText: theme.dividerText,
      pageSurface: theme.primaryGray,
      cardSurface: theme.primaryWhite,
      elementSurface: theme.offWhite,
      hoverSurface: theme.secondaryHover,
      activeSurface: theme.lightGray,
      controlBorder: theme.lightGray,
      chipHoverSurface: theme.sidebarHoverGray,
      separator: theme.separatorGray,
      selectionText: theme.primaryBlue,
      controlAccent: theme.primaryBlue,
      selectionSurface: theme.lightBlue,
      errorSurface: theme.lightRed,
      errorText: theme.primaryWhite,
      errorBorder: theme.errorRed,
      disabledSurface: theme.primaryDisabledGray,
      mutedIcon: theme.tertiaryGray,
      onAction: theme.primaryBlack,
      action: theme.limeYellow,
      actionHover: theme.limeYellowActive,
      actionActive: theme.secondaryGreen,
      radius: theme.defaultBorderRadius,
      shadow: theme.defaultShadow,
      fontFamily: tokens.typography.fontFamily.primary
    };
  }

  const { text, surface, icon, border } = tokens.theme[theme.mode];

  return {
    rootText: text.body,
    bodyText: text.body,
    headingText: text.heading,
    mutedText: text.secondary,
    pageSurface: surface.main.page,
    cardSurface: surface.main.cards,
    elementSurface: surface.main['element-on-card'],
    hoverSurface: surface.secondary['action-hover'],
    activeSurface: surface.secondary.background,
    controlBorder: border.primary,
    chipHoverSurface: surface.main['element-on-container'],
    separator: border.primary,
    selectionText: text.information,
    controlAccent: surface.information.action,
    selectionSurface: surface.information.background,
    errorSurface: surface.error.background,
    // Legacy painted primaryWhite on a pale pink bar — white on #FFECF0. The token pair is the fix.
    errorText: text.error,
    errorBorder: border.error,
    disabledSurface: surface.disabled.action,
    mutedIcon: icon.secondary,
    onAction: text.onAction,
    action: surface.primary.action,
    actionHover: surface.primary['action-hover'],
    actionActive: surface.primary['action-focused'],
    radius: tokens.spacing.radius['100'],
    shadow: tokens.shadows.dropdown,
    /**
     * The embed iframe is served from the ThoughtSpot origin and nothing ships it an @font-face or a
     * `customCSSUrl` — and our own faces are vite-bundled under hashed names, so no stable URL exists
     * for it to load. This value has therefore only ever selected a fallback. Naming the stack the app
     * itself falls back to puts the embed near the rest of the product instead of on ThoughtSpot's
     * default. Switch-off keeps the bare family, because a different typeface is a visible change.
     * Serving the real file needs `customCSSUrl` plus a font-licence and CORS review — see the PR.
     */
    fontFamily: `${primaryFontFamily(theme.isNewDesign)}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`
  };
};
